import React, { useState, useEffect, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Loader2, Sparkles, CheckCircle, Users, RotateCw } from "lucide-react";
import RequestCard from "./RequestCard";

export default function BlueprintRequests() {
    const [requests, setRequests] = useState([]);
    const [stats, setStats] = useState({ pendingCount: 0, totalUsersCategorized: 0 });
    const [filter, setFilter] = useState("pending");

    const { call: fetchRequests, isLoading } = useBluelagoon({
        method: "getBlueprintRequests",
    });
    const { call: triggerCategorization, isLoading: triggering } =
        useBluelagoon({ method: "triggerBlueprintCategorization" });

    const loadRequests = useCallback(async () => {
        const res = await fetchRequests({ status: filter });
        if (res?.data) {
            setRequests(res.data.requests || []);
            if (res.data.stats) setStats(res.data.stats);
        }
    }, [fetchRequests, filter]);

    useEffect(() => {
        loadRequests();
    }, [loadRequests]);

    const handleTrigger = async () => {
        const res = await triggerCategorization({});
        if (res?.data) {
            alert(res.data.message);
            setTimeout(loadRequests, 2000);
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Blueprint Requests</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        AI-generated suggestions based on what users want to build
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={loadRequests}
                        disabled={isLoading}
                    >
                        <RotateCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                    <Button onClick={handleTrigger} disabled={triggering}>
                        {triggering ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Sparkles className="h-4 w-4 mr-2" />
                        )}
                        Run Categorization
                    </Button>
                </div>
            </div>

            <StatsBar pendingCount={stats.pendingCount} totalUsers={stats.totalUsersCategorized} />

            <div className="flex gap-2">
                {["pending", "done"].map((s) => (
                    <Button
                        key={s}
                        variant={filter === s ? "default" : "outline"}
                        size="sm"
                        onClick={() => setFilter(s)}
                    >
                        {s === "pending" ? "Pending" : "Done"}
                    </Button>
                ))}
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : requests.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                        No {filter} requests. Run categorization to analyze new users.
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4">
                    {requests.map((req) => (
                        <RequestCard
                            key={req.id}
                            request={req}
                            onUpdate={loadRequests}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function StatsBar({ pendingCount, totalUsers }) {
    return (
        <div className="grid grid-cols-2 gap-4">
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                        Pending Requests
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-amber-500" />
                        {pendingCount}
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                        Total Users Categorized
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold flex items-center gap-2">
                        <Users className="h-5 w-5 text-blue-500" />
                        {totalUsers}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
